import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext.jsx';
import { loadWithRetry } from '../lib/loadWithRetry.js';
import { SUPPORTED_LANGUAGES } from '../i18n.js';
import {
  availableLanguages,
  fetchIssue,
  fetchIssues,
  findNeighbours,
  formatIssueMonth,
  issueContent,
  issuePath,
  parsePeriod,
} from '../lib/newsletter.js';
import Markdown from '../components/common/Markdown.jsx';
import NotFound from './NotFound.jsx';

export default function NewsletterIssue() {
  const { year: yearParam, month: monthParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { isAdmin, isSignedIn } = useAuth();
  const period = parsePeriod(yearParam, monthParam);
  const [issue, setIssue] = useState(undefined); // undefined = loading, null = not found
  const [neighbours, setNeighbours] = useState({ newer: null, older: null });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!period) return undefined;
    let cancelled = false;
    setIssue(undefined);
    setError(null);
    window.scrollTo({ top: 0 });
    loadWithRetry(() => fetchIssue(period.year, period.month), { label: 'newsletter-issue' })
      .then((row) => {
        if (cancelled) return;
        setIssue(row ?? null);
        if (row) {
          // Neighbour lookup is best-effort; a failure just hides prev/next.
          fetchIssues()
            .then((all) => !cancelled && setNeighbours(findNeighbours(all, row)))
            .catch(() => {});
        }
      })
      .catch((err) => !cancelled && setError(err.message ?? String(err)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearParam, monthParam]);

  // Language resolution. `?lang=xx` (used by the email's language links)
  // wins and also switches the site chrome so the whole page matches;
  // otherwise the issue follows the site language selector. Either way we
  // fall back to English when that version doesn't exist.
  const siteLang = (i18n.resolvedLanguage ?? 'en').slice(0, 2);
  const paramLang = searchParams.get('lang')?.slice(0, 2) ?? null;
  const validParam = paramLang && SUPPORTED_LANGUAGES.some((l) => l.code === paramLang) ? paramLang : null;

  useEffect(() => {
    if (validParam && validParam !== siteLang) i18n.changeLanguage(validParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validParam]);

  const requestedLang = validParam ?? siteLang;
  const available = useMemo(() => (issue ? availableLanguages(issue) : ['en']), [issue]);
  const content = useMemo(() => (issue ? issueContent(issue, requestedLang) : null), [issue, requestedLang]);

  function pickLanguage(code) {
    const next = new URLSearchParams(searchParams);
    if (code === siteLang) next.delete('lang');
    else next.set('lang', code);
    setSearchParams(next, { replace: true });
  }

  if (!period || issue === null) return <NotFound />;

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-ink-secondary">{error}</p>
      </div>
    );
  }
  if (issue === undefined || !content) {
    return (
      <div role="status" aria-live="polite" className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center text-ink-secondary">
        {t('common.loading')}
      </div>
    );
  }

  const monthLabel = formatIssueMonth(issue.year, issue.month, i18n.resolvedLanguage);
  const isDraft = issue.status !== 'published';
  const fellBack = content.lang !== requestedLang;
  const requestedName = SUPPORTED_LANGUAGES.find((l) => l.code === requestedLang)?.name ?? requestedLang;

  return (
    <article className="mx-auto max-w-3xl px-6 pb-20 pt-28" lang={content.lang}>
      <nav className="mb-6 text-sm text-ink-muted">
        <Link to="/newsletter" className="hover:text-gold-light">
          {t('newsletter.backToList')}
        </Link>
      </nav>

      <header className="mb-10 border-b border-gold/25 pb-8">
        <div className="flex flex-wrap items-center gap-3">
          <div className="section-label !mb-0">{monthLabel}</div>
          {isDraft && (
            <span className="rounded-full border border-gold/40 bg-navy-mid/40 px-2 py-0.5 text-[0.65rem] font-medium uppercase tracking-[0.12em] text-gold-light">
              {t('newsletter.status.draft')}
            </span>
          )}
        </div>
        <h1 className="mt-3 font-serif text-4xl font-semibold leading-tight text-cream md:text-5xl">
          {content.title}
        </h1>
        {content.summary && <p className="mt-4 text-lg text-ink-secondary">{content.summary}</p>}

        {available.length > 1 && (
          <div className="mt-6" role="group" aria-label={t('newsletter.language.label')}>
            <span className="mr-3 text-xs font-medium uppercase tracking-[0.12em] text-ink-muted">
              {t('newsletter.language.label')}
            </span>
            <span className="inline-flex flex-wrap gap-2 align-middle">
              {SUPPORTED_LANGUAGES.filter((l) => available.includes(l.code)).map((l) => (
                <button
                  key={l.code}
                  type="button"
                  lang={l.code}
                  aria-pressed={content.lang === l.code}
                  onClick={() => pickLanguage(l.code)}
                  className={`rounded-full border px-3 py-1 text-sm transition ${
                    content.lang === l.code
                      ? 'border-gold/60 bg-navy-mid/60 text-gold-light'
                      : 'border-white/10 bg-navy-deep/40 text-ink-secondary hover:border-gold/30'
                  }`}
                >
                  {l.name}
                </button>
              ))}
            </span>
          </div>
        )}
        {fellBack && (
          <p className="mt-4 text-sm text-ink-muted">
            {t('newsletter.language.fallback', { language: requestedName })}
          </p>
        )}

        {isAdmin && (
          <div className="mt-5">
            <Link to={`/newsletter/${issue.id}/edit`} className="btn-ghost text-sm">
              {t('newsletter.editIssue')}
            </Link>
          </div>
        )}
      </header>

      <Markdown source={content.body} />

      <footer className="mt-14 border-t border-white/10 pt-8">
        <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
          {neighbours.older ? (
            <Link to={issuePath(neighbours.older)} className="card-link">
              ← {formatIssueMonth(neighbours.older.year, neighbours.older.month, i18n.resolvedLanguage)}
            </Link>
          ) : (
            <span />
          )}
          {neighbours.newer && (
            <Link to={issuePath(neighbours.newer)} className="card-link">
              {formatIssueMonth(neighbours.newer.year, neighbours.newer.month, i18n.resolvedLanguage)} →
            </Link>
          )}
        </div>

        <aside className="mt-10 rounded-xl border border-gold/20 bg-navy-base/40 p-6 text-sm text-ink-secondary">
          <h2 className="mb-2 font-serif text-lg text-cream">{t('newsletter.subscribe.title')}</h2>
          <p>{t('newsletter.subscribe.body')}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            {!isSignedIn && (
              <Link to="/auth" className="btn-ghost text-sm">
                {t('nav.register')}
              </Link>
            )}
            <Link to="/whats-new" className="btn-ghost text-sm">
              {t('home.tiles.whats_new.cta')}
            </Link>
          </div>
        </aside>
      </footer>
    </article>
  );
}
